// daemon game profile API 的 TS 类型与 PUT/POST 调用封装。
//
// 对应 daemon/internal/store/game_profile.go 的 GameProfile / GameContinent /
// GameMap / GameProfileBundle，以及 daemon/internal/web/game_profile.go 的
// handler 输入约定（支持 application/json 与 form-encoded 两种 Content-Type）。
//
// 注意：所有 RawJSON 字段在传输层都是 JSON value（不是字符串）；upsert 时
// 必须直接放对应 value，daemon 会以 json.RawMessage 透传到 store。

import { apiFetch, apiPost, apiPostForm } from "./client";
import {
  API_GAMES,
  apiCurrencies,
  apiCurrencyDelete,
  apiCurrencyItem,
  apiContinentDelete,
  apiContinents,
  apiGameBundle,
  apiGameJSONField,
  apiGameModule,
  apiGameModuleClear,
  apiGameModuleReferences,
  apiGameModules,
  apiGameProfitItems,
  apiGameProfitInstances,
  apiGameProfitMaps,
  apiGameServers,
  apiProfitSourceRules,
  apiProfitValueRules,
  apiAutoMapHuntLeases,
  apiAutoMapHuntLeaseRelease,
  apiMapRespawnTargets,
  apiMaterials,
  apiMapRespawnStats,
  apiMaterialDelete,
  apiMaterialItem,
  apiGameUpsert,
  apiMapItem,
  apiMapDelete,
  apiMaps,
  apiServerGroupMember,
  apiServerGroupRename,
  apiServerGroups,
} from "./endpoints";
import type {
  AutoMapHuntLeaseReleaseResponse,
  AutoMapHuntLeasesResponse,
  GameServerEditResponse,
  GameServersResponse,
  GameModuleEditResponse,
  GameModuleReferencesResponse,
  GameModulesResponse,
  MapRespawnStatsResponse,
  MapRespawnTargetEditResponse,
  ProfitGameItemsWindow,
  ProfitGameInstancesWindow,
  ProfitGameMapsWindow,
  ProfitSourceRuleEditResponse,
  ProfitSourceRulesResponse,
  ProfitValueRuleEditResponse,
  ProfitValueRulesResponse,
  ServerGroupEditResponse,
  ServerGroupsResponse,
} from "./types";

export interface GameProfileSummary {
  game_id: string;
  display_name: string;
  notes: string;
  version: number;
  created_at: number;
  updated_at: number;
  map_count?: number;
  currency_count?: number;
  material_count?: number;
  account_count?: number;
  continent_count?: number;
}

export interface GameProfile extends GameProfileSummary {
  global: unknown;
  protect: unknown;
  patrolTemplates?: unknown;
  assistedCruiseRandomFly?: unknown;
}

export interface GameContinent {
  id: number;
  game_id: string;
  continent_id: string;
  name: string;
  enabled: boolean;
  enter: unknown;
  enterConditions: unknown;
  sort_order: number;
  version: number;
  created_at: number;
  updated_at: number;
  map_count?: number;
}

export interface GameMapCore {
  id: number;
  game_id: string;
  map_id: string;
  name: string;
  continent_id: string;
  edges: unknown;
  nested_group: string;
  respawn_model: string;
  boss_names: unknown;
  selectable_default: boolean;
  allow_transit_when_cooldown: boolean;
  sort_order: number;
  version: number;
  created_at: number;
  updated_at: number;
  unreachable?: boolean;
  unreachable_reason?: string;
}

export interface GameMap extends GameMapCore {
  match: unknown;
  enter: unknown;
  enterConditions: unknown;
  fight: unknown;
}

export type GameMapSummary = GameMapCore;

export interface CurrencyWatchRecord {
  id: number;
  game_id?: string;
  currency_id: number;
  label?: string;
  sort_order: number;
  created_at?: number;
  updated_at?: number;
}

export interface MaterialWatchRecord {
  id: number;
  game_id?: string;
  item_id: number;
  label?: string;
  sort_order: number;
  created_at?: number;
  updated_at?: number;
}

export interface GameProfileBundle {
  profile: GameProfile;
  game_id: string;
  display_name: string;
  version: number;
  global: unknown;
  protect: unknown;
  patrolTemplates?: unknown;
  assistedCruiseRandomFly?: unknown;
  continents: GameContinent[];
  maps: GameMapSummary[];
  currencies: CurrencyWatchRecord[];
  materials: MaterialWatchRecord[];
}

export interface GamesListResponse {
  ok: boolean;
  games: GameProfileSummary[];
}

export interface GameBundleResponse {
  ok: boolean;
  bundle: GameProfileBundle;
}

export interface GameUpsertInput {
  game_id: string;
  display_name: string;
  notes?: string;
  global?: unknown;
  protect?: unknown;
  patrolTemplates?: unknown;
  assistedCruiseRandomFly?: unknown;
  version?: number;
}

export interface ContinentUpsertInput {
  id?: number;
  continent_id: string;
  name: string;
  enabled: boolean;
  enter?: unknown;
  enterConditions?: unknown;
  sort_order?: number;
  version?: number;
}

export interface MapUpsertInput {
  id?: number;
  map_id: string;
  name: string;
  continent_id: string;
  match?: unknown;
  enter?: unknown;
  enterConditions?: unknown;
  edges?: unknown;
  nested_group?: string;
  respawn_model?: string;
  boss_names?: unknown;
  fight?: unknown;
  selectable_default?: boolean;
  allow_transit_when_cooldown?: boolean;
  sort_order?: number;
  version?: number;
}

export interface WatchListInput {
  game_id: string;
  numeric_id: number;
  label?: string;
  sort_order?: number;
  item_name?: string;
}

export interface ProfitValueRuleInput {
  kind: "currency" | "material";
  ref_id: string;
  unit_value: number;
  value_currency?: string;
  source?: string;
  enabled?: boolean;
}

export interface ProfitSourceRuleInput {
  module?: string;
  source_type: string;
  include_in_profit: boolean;
  include_in_cost: boolean;
  note?: string;
}

export type GameProfitInstancesResponse = ProfitGameInstancesWindow & {
  ok: boolean;
};

export type GameProfitMapsResponse = ProfitGameMapsWindow & {
  ok: boolean;
};

export type GameProfitItemsResponse = ProfitGameItemsWindow & {
  ok: boolean;
};

export interface GameProfitRankingQuery {
  window?: "60m" | "24h" | "today";
  serverID?: string;
  limit?: number;
}

export interface ServerGroupUpsertInput {
  canonical_server_id: string;
  members: string[];
  note?: string;
}

// ===== Query / Mutation 封装 =====

export function listGames() {
  return apiFetch<GamesListResponse>(API_GAMES);
}

export function getGameBundle(gameID: string) {
  return apiFetch<GameBundleResponse>(apiGameBundle(gameID));
}

export function getGameMap(gameID: string, mapID: string) {
  return apiFetch<{ ok: boolean; map: GameMap }>(apiMapItem(gameID, mapID));
}

export function upsertGame(input: GameUpsertInput) {
  // 创建新 profile 时 path 用 "/api/games"（POST 根集合）
  const path = input.game_id ? apiGameUpsert(input.game_id) : API_GAMES;
  return apiPost<{ ok: boolean; game: GameProfile }>(path, input);
}

export function updateGameJSONField(
  gameID: string,
  field: "global" | "protect" | "patrolTemplates" | "assistedCruiseRandomFly",
  value: unknown,
  version: number,
) {
  const pathField =
    field === "patrolTemplates"
      ? "patrol_templates"
      : field === "assistedCruiseRandomFly"
        ? "assisted_cruise_random_fly"
        : field;
  return apiFetch<{ ok: boolean; game: GameProfile }>(
    apiGameJSONField(gameID, pathField),
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ [field]: value, version }),
    },
  );
}

export function listGameModules(gameID: string) {
  return apiFetch<GameModulesResponse>(apiGameModules(gameID));
}

export function updateGameModuleConfig(
  gameID: string,
  module: string,
  desiredConfigJSON: string,
) {
  return apiPostForm<GameModuleEditResponse>(apiGameModule(gameID, module), {
    desired_config_json: desiredConfigJSON,
  });
}

export function clearGameModuleConfig(gameID: string, module: string) {
  return apiPostForm<GameModuleEditResponse>(apiGameModuleClear(gameID, module), {});
}

export function getGameModuleReferences(gameID: string, module: string) {
  return apiFetch<GameModuleReferencesResponse>(
    apiGameModuleReferences(gameID, module),
  );
}

export function upsertContinent(gameID: string, input: ContinentUpsertInput) {
  return apiPost<{ ok: boolean; continent: GameContinent }>(
    apiContinents(gameID),
    input,
  );
}

export function deleteContinent(
  gameID: string,
  continentID: string,
  version: number,
) {
  return apiFetch<{ ok: boolean }>(apiContinentDelete(gameID, continentID), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      version: String(version),
      _method: "DELETE",
    }).toString(),
  });
}

export function upsertMap(gameID: string, input: MapUpsertInput) {
  return apiPost<{ ok: boolean; map: GameMap }>(apiMaps(gameID), input);
}

export function deleteMap(gameID: string, mapID: string, version: number) {
  return apiFetch<{ ok: boolean }>(apiMapDelete(gameID, mapID), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      version: String(version),
      _method: "DELETE",
    }).toString(),
  });
}

export function listServerGroups(gameID: string) {
  return apiFetch<ServerGroupsResponse>(apiServerGroups(gameID));
}

export function upsertServerGroup(gameID: string, input: ServerGroupUpsertInput) {
  return apiPost<ServerGroupEditResponse>(apiServerGroups(gameID), input);
}

export function removeServerGroupMember(gameID: string, memberID: string) {
  return apiPostForm<ServerGroupEditResponse>(apiServerGroupMember(gameID, memberID), {
    _method: "DELETE",
  });
}

export function renameServerGroup(
  gameID: string,
  canonicalID: string,
  newCanonicalID: string,
) {
  return apiPost<ServerGroupEditResponse>(apiServerGroupRename(gameID, canonicalID), {
    new_canonical_server_id: newCanonicalID,
  });
}

export function getMapRespawnStats(gameID: string, serverID: string) {
  return apiFetch<MapRespawnStatsResponse>(apiMapRespawnStats(gameID, serverID));
}

export function updateMapRespawnTarget(
  gameID: string,
  input: {
    server_id?: string;
    canonical_server_id?: string;
    map_id: string;
    target_key: string;
    display_name?: string;
    kind?: string;
    model?: string;
    disabled?: boolean;
    priority?: number;
    merged_into_target_key?: string;
  },
) {
  return apiPost<MapRespawnTargetEditResponse>(apiMapRespawnTargets(gameID), input);
}

export function searchGameServers(gameID: string, query = "", limit = 10) {
  return apiFetch<GameServersResponse>(apiGameServers(gameID, query, limit));
}

export function upsertGameServer(
  gameID: string,
  input: {
    server_id: string;
    server_name?: string;
    display_name?: string;
    alias?: string;
    metadata_json?: string;
  },
) {
  return apiPostForm<GameServerEditResponse>(apiGameServers(gameID), {
    server_id: input.server_id,
    server_name: input.server_name,
    display_name: input.display_name,
    alias: input.alias,
    metadata_json: input.metadata_json,
    source: "manual",
  });
}

export function listAutoMapHuntLeases(gameID: string, serverID = "") {
  return apiFetch<AutoMapHuntLeasesResponse>(apiAutoMapHuntLeases(gameID, serverID));
}

export function releaseAutoMapHuntLease(
  gameID: string,
  input: { instance_id: string; conflict_key?: string },
) {
  return apiPost<AutoMapHuntLeaseReleaseResponse>(
    apiAutoMapHuntLeaseRelease(gameID),
    input,
  );
}

export function listProfitValueRules(gameID: string) {
  return apiFetch<ProfitValueRulesResponse>(apiProfitValueRules(gameID));
}

export function getGameProfitInstances(
  gameID: string,
  query: GameProfitRankingQuery = {},
) {
  return apiFetch<GameProfitInstancesResponse>(
    apiGameProfitInstances(
      gameID,
      query.window ?? "60m",
      query.serverID ?? "",
      query.limit ?? 20,
    ),
  );
}

export function getGameProfitMaps(
  gameID: string,
  query: GameProfitRankingQuery = {},
) {
  return apiFetch<GameProfitMapsResponse>(
    apiGameProfitMaps(
      gameID,
      query.window ?? "60m",
      query.serverID ?? "",
      query.limit ?? 20,
    ),
  );
}

export function getGameProfitItems(
  gameID: string,
  query: GameProfitRankingQuery = {},
) {
  return apiFetch<GameProfitItemsResponse>(
    apiGameProfitItems(
      gameID,
      query.window ?? "60m",
      query.serverID ?? "",
      query.limit ?? 20,
    ),
  );
}

export function upsertProfitValueRule(gameID: string, input: ProfitValueRuleInput) {
  return apiPost<ProfitValueRuleEditResponse>(apiProfitValueRules(gameID), input);
}

export function listProfitSourceRules(gameID: string) {
  return apiFetch<ProfitSourceRulesResponse>(apiProfitSourceRules(gameID));
}

export function upsertProfitSourceRule(gameID: string, input: ProfitSourceRuleInput) {
  return apiPost<ProfitSourceRuleEditResponse>(apiProfitSourceRules(gameID), input);
}

export function createCurrencyWatch(input: WatchListInput) {
  return apiPostForm<{ ok: boolean; item: CurrencyWatchRecord }>(apiCurrencies, {
    game_id: input.game_id,
    currency_id: input.numeric_id,
    label: input.label ?? "",
    sort_order: input.sort_order ?? 0,
  });
}

export function updateCurrencyWatch(id: number, input: WatchListInput) {
  return apiPostForm<{ ok: boolean; id: number; action: string }>(
    apiCurrencyItem(id),
    {
      game_id: input.game_id,
      label: input.label ?? "",
      sort_order: input.sort_order ?? 0,
    },
  );
}

export function deleteCurrencyWatch(id: number) {
  return apiPostForm<{ ok: boolean; id: number; action: string }>(
    apiCurrencyDelete(id),
    {},
  );
}

export function createMaterialWatch(input: WatchListInput) {
  return apiPostForm<{ ok: boolean; item: MaterialWatchRecord }>(apiMaterials, {
    game_id: input.game_id,
    item_id: input.numeric_id || undefined,
    item_name: input.item_name ?? "",
    label: input.label ?? "",
    sort_order: input.sort_order ?? 0,
  });
}

export function updateMaterialWatch(id: number, input: WatchListInput) {
  return apiPostForm<{ ok: boolean; id: number; action: string }>(
    apiMaterialItem(id),
    {
      game_id: input.game_id,
      label: input.label ?? "",
      sort_order: input.sort_order ?? 0,
    },
  );
}

export function deleteMaterialWatch(id: number) {
  return apiPostForm<{ ok: boolean; id: number; action: string }>(
    apiMaterialDelete(id),
    {},
  );
}
